/**
 * MarketsRefreshControl — 2026-06-13 ui-markets-card-refresh.
 *
 * Compact per-card refresh button + optional freshness text for the
 * Markets page cards. Reuses the existing refreshEvents bus copy.
 *
 * Deliberately tiny: an icon button (RefreshCw, spins while refreshing)
 * with a "Refresh" affordance and an optional "Updated h:mm AM" note.
 * No banners, no panels, no status pills.
 */
import React from "react";
import { RefreshCw } from "lucide-react";

function _formatUpdated(ts: number | null | undefined): string | null {
  if (!ts) return null;
  try {
    const t = new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return `Updated ${t}`;
  } catch {
    return null;
  }
}

export default function MarketsRefreshControl({
  onRefresh,
  refreshing = false,
  lastUpdatedAt = null,
  testid,
  showLabel = true,
}: {
  onRefresh: () => void;
  refreshing?: boolean;
  lastUpdatedAt?: number | null;
  testid: string;
  showLabel?: boolean;
}) {
  const updated = _formatUpdated(lastUpdatedAt);
  return (
    <div className="flex items-center gap-2">
      {updated && (
        <span
          data-testid={testid + "-updated"}
          className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)] hidden sm:inline"
        >
          {refreshing ? "Refreshing…" : updated}
        </span>
      )}
      <button
        type="button"
        data-testid={testid}
        onClick={() => { if (!refreshing) onRefresh(); }}
        disabled={refreshing}
        title="Refresh this section"
        aria-label="Refresh this section"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border border-[var(--card-border)] bg-[var(--input-bg)] text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--card-border)]/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
        {showLabel && <span className="hidden md:inline">{refreshing ? "Refreshing…" : "Refresh"}</span>}
      </button>
    </div>
  );
}
